import { ApiGraphData } from "./SimulationResults";
import { getSignalSeries, getSignalStyle, LegendItem, Series } from "./SimulationGraph";
import { msToSeconds } from "../utils/time";

// Axis choices for the signal-vs-signal views: time plus every I/O signal, by
// legend name.
export const TIME_AXIS = "Time [s]";

export const getAxisOptions = (data: ApiGraphData): string[] => [TIME_AXIS, ...Array.from(getSignalSeries(data).keys())];

// One variable per axis: the signals one trace draws against each other.
export type TraceAxes<K extends string> = Record<K, string>;

// The traces to draw, each pairing one variable per axis.
export type AxisSelection<K extends string> = TraceAxes<K>[];

// Keep the chosen traces whose variables all still exist; with none left, use
// the defaults.
export const resolveSelection = <K extends string>(
  selected: AxisSelection<K>,
  defaults: AxisSelection<K>,
  options: string[],
  order: K[],
): AxisSelection<K> => {
  const resolved = selected.filter((axes) => (
    typeof axes === "object" && axes !== null && order.every((axis) => options.includes(axes[axis]))
  ));

  return resolved.length > 0 ? resolved : defaults;
};

export interface AxisTrace<K extends string> extends LegendItem {
  axes: Record<K, string>;
}

export const getAxisTraces = <K extends string>(selection: AxisSelection<K>, order: K[]): AxisTrace<K>[] => {
  return selection.map((axes, index) => ({
    name: order.map((axis) => axes[axis]).join(" · "),
    axes,
    ...getSignalStyle(index),
  }));
};

// Signals are sampled at their own timestamps and hold their value in between
// (the 2D graph draws them as steps), so bring the chosen ones onto a shared
// timeline by zero-order hold. Timestamps before a signal's first sample have
// no value and are left out.
const sampleOnCommonTimeline = (series: Series[], extraTimes: number[] = []): { time: number[]; values: number[][] } => {
  const timeline = Array.from(new Set([...series.flatMap((item) => item.x), ...extraTimes])).sort((a, b) => a - b);
  const cursors = series.map(() => -1);
  const time: number[] = [];
  const values: number[][] = series.map(() => []);

  timeline.forEach((t) => {
    series.forEach((item, index) => {
      while (cursors[index] + 1 < item.x.length && item.x[cursors[index] + 1] <= t) {
        cursors[index] += 1;
      }
    });

    if (cursors.some((cursor) => cursor < 0)) {
      return;
    }

    time.push(t);
    series.forEach((item, index) => values[index].push(item.y[cursors[index]]));
  });

  return { time, values };
};

export interface SampledAxes {
  seconds: number[];
  // Values per chosen axis name, aligned with `seconds`.
  columns: Map<string, number[]>;
}

const STEP_SIZE_PARAMETER = "CppModel.SimulationStepSize";
// Upper bound on the samples per trace when expanding to simulation steps.
const MAX_STEP_SAMPLES = 20000;

// Every simulation step from 0 to `end` (ms). Series only hold the points where
// a value changed, so this brings back the steps in between. Very long runs
// are thinned to whole multiples of the step size.
const getStepTimes = (data: ApiGraphData, end: number): number[] => {
  const stepMs = (data.parameters[STEP_SIZE_PARAMETER] ?? 0) * 1000;

  if (!(stepMs > 0) || !(end > 0)) {
    return [];
  }

  const stride = stepMs * Math.max(1, Math.ceil(end / stepMs / MAX_STEP_SAMPLES));
  const count = Math.floor(end / stride);

  // Rounded so float steps (e.g. 0.01 s) land on whole-ms change times.
  return Array.from({ length: count + 1 }, (_, index) => Number((index * stride).toPrecision(12)));
};

// Sample the named axes (signals or TIME_AXIS) on one shared timeline in
// seconds: the times any of them changed, or with `everyStep` every simulation
// step.
export const sampleAxes = (data: ApiGraphData, names: string[], everyStep = false): SampledAxes => {
  const signals = getSignalSeries(data);
  const chosen = Array.from(new Set(names.filter((name) => signals.has(name))));
  const series = chosen.map((name) => signals.get(name) as Series);
  const allTimes = Array.from(signals.values()).flatMap((item) => item.x);
  const stepTimes = everyStep ? getStepTimes(data, Math.max(0, ...allTimes)) : [];

  // With only time chosen there is no signal to sample; use every timestamp.
  const { time, values } = series.length > 0
    ? sampleOnCommonTimeline(series, stepTimes)
    : { time: Array.from(new Set([...allTimes, ...stepTimes])).sort((a, b) => a - b), values: [] };

  const seconds = time.map(msToSeconds);
  const columns = new Map(chosen.map((name, index) => [name, values[index]]));
  columns.set(TIME_AXIS, seconds);

  return { seconds, columns };
};

// The variables shown along one axis, across all traces.
export const getAxisTitle = <K extends string>(selection: AxisSelection<K>, axis: K) => (
  Array.from(new Set(selection.map((axes) => axes[axis]))).join(", ")
);
